import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { ErrorState } from './error-state'

interface ErrorBoundaryClassProps {
  children: ReactNode
  /** Changing this value while an error is shown clears it (e.g. route change). */
  resetKey: unknown
}

interface ErrorBoundaryClassState {
  error: Error | null
}

class ErrorBoundaryClass extends Component<ErrorBoundaryClassProps, ErrorBoundaryClassState> {
  state: ErrorBoundaryClassState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryClassState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // oxlint-disable-next-line no-console
    console.error('ErrorBoundary caught an error', error, info.componentStack)
  }

  componentDidUpdate(prevProps: ErrorBoundaryClassProps): void {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  handleRetry = (): void => {
    this.setState({ error: null })
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-4">
          <ErrorState
            title="Ocurrió un error inesperado"
            message="La aplicación encontró un problema al mostrar esta pantalla. Podés reintentar o navegar a otra sección."
            onRetry={this.handleRetry}
          />
        </div>
      )
    }

    return this.props.children
  }
}

export interface ErrorBoundaryProps {
  children: ReactNode
}

/**
 * Global render-error boundary. Wraps `resetKey` around the current route so
 * navigating away from a broken screen recovers automatically, in addition
 * to the manual "Reintentar" button in the fallback.
 */
export function ErrorBoundary({ children }: ErrorBoundaryProps) {
  const location = useLocation()

  return <ErrorBoundaryClass resetKey={location.pathname}>{children}</ErrorBoundaryClass>
}
